import React, {useCallback, useEffect, useMemo, useState} from "react";
import useApiStable from "../../utils/useApiStable";

const ENCOUNTER_LABELS = {
    EINZEL: "Einzel",
    DOPPEL: "Doppel",
};

const getNormalizedRank = (rank) => {
    if (rank === null || rank === undefined || rank === "") {
        return Number.MAX_SAFE_INTEGER;
    }

    const normalized = Number(rank);
    return Number.isFinite(normalized) ? normalized : Number.MAX_SAFE_INTEGER;
};

const getRankLabel = (rank) => rank === null || rank === undefined || rank === "" ? "—" : rank;

const MatchLineupEditor = ({matchId, teamId, isCaptain}) => {
    const api = useApiStable();
    const [licensedPlayers, setLicensedPlayers] = useState([]);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [players, lineupData] = await Promise.all([
                api.get(`/league/teams/${teamId}/licensed-players`),
                api.get(`/league/teams/${teamId}/matches/${matchId}/lineup`),
            ]);
            setLicensedPlayers(players || []);
            setEntries(lineupData?.entries?.map(e => ({...e})) || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [matchId, teamId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const licensedPlayersById = useMemo(
        () => new Map(licensedPlayers.map(player => [String(player.idNumber), player])),
        [licensedPlayers]
    );

    const sortedLicensedPlayers = useMemo(
        () => licensedPlayers
            .map((player, index) => ({player, index}))
            .sort((left, right) => {
                const rankDifference = getNormalizedRank(left.player.rank) - getNormalizedRank(right.player.rank);
                if (rankDifference !== 0) {
                    return rankDifference;
                }

                const nameDifference = (left.player.name || "").localeCompare(right.player.name || "", "de", {
                    sensitivity: "base",
                });
                if (nameDifference !== 0) {
                    return nameDifference;
                }

                return left.index - right.index;
            })
            .map(({player}) => player),
        [licensedPlayers]
    );

    const sanitizeEntriesByRankOrder = useCallback((inputEntries) => {
        const sanitizedByKey = new Map();

        ["EINZEL", "DOPPEL"].forEach(encounterType => {
            let minimumAllowedRank = null;

            inputEntries
                .filter(entry => entry.encounterType === encounterType)
                .sort((a, b) => a.position - b.position)
                .forEach(entry => {
                    const sanitizePlayer = (player) => {
                        if (!player) {
                            return null;
                        }
                        if (minimumAllowedRank !== null && getNormalizedRank(player.rank) < minimumAllowedRank) {
                            return null;
                        }
                        return player;
                    };

                    const sanitizedEntry = {
                        ...entry,
                        player1: sanitizePlayer(entry.player1),
                        player2: sanitizePlayer(entry.player2),
                    };

                    const currentRanks = [sanitizedEntry.player1, sanitizedEntry.player2]
                        .filter(Boolean)
                        .map(player => getNormalizedRank(player.rank));

                    if (currentRanks.length > 0) {
                        const currentMaxRank = Math.max(...currentRanks);
                        minimumAllowedRank = minimumAllowedRank === null
                            ? currentMaxRank
                            : Math.max(minimumAllowedRank, currentMaxRank);
                    }

                    sanitizedByKey.set(`${encounterType}-${entry.position}`, sanitizedEntry);
                });
        });

        return inputEntries.map(entry => sanitizedByKey.get(`${entry.encounterType}-${entry.position}`) || entry);
    }, []);

    const getMinimumAllowedRankForPosition = useCallback((encounterType, position) => {
        const previousRanks = entries
            .filter(entry => entry.encounterType === encounterType && entry.position < position)
            .flatMap(entry => [entry.player1, entry.player2])
            .filter(Boolean)
            .map(player => getNormalizedRank(player.rank));

        if (previousRanks.length === 0) {
            return null;
        }

        return Math.max(...previousRanks);
    }, [entries]);

    const getSelectablePlayers = useCallback((encounterType, position, field, currentIdNumber) => {
        const minimumAllowedRank = getMinimumAllowedRankForPosition(encounterType, position);

        // Only exclude players already used within the same encounter type (EINZEL or DOPPEL).
        // A player may appear in both EINZEL and DOPPEL, but not twice within the same type.
        const usedIdNumbers = new Set();
        entries.forEach(entry => {
            if (entry.encounterType !== encounterType) return;
            const isSameSlot = entry.position === position;
            if (!isSameSlot) {
                if (entry.player1?.idNumber != null) usedIdNumbers.add(String(entry.player1.idNumber));
                if (entry.player2?.idNumber != null) usedIdNumbers.add(String(entry.player2.idNumber));
            } else {
                const otherField = field === "player1" ? "player2" : "player1";
                if (entry[otherField]?.idNumber != null) usedIdNumbers.add(String(entry[otherField].idNumber));
            }
        });

        return sortedLicensedPlayers.filter(player => {
            if (String(player.idNumber) === currentIdNumber) return true;
            if (usedIdNumbers.has(String(player.idNumber))) return false;
            if (minimumAllowedRank === null) return true;
            return getNormalizedRank(player.rank) >= minimumAllowedRank;
        });
    }, [getMinimumAllowedRankForPosition, sortedLicensedPlayers, entries]);

    const updateEntry = (encounterType, position, field, idNumberStr) => {
        setEntries(prev => sanitizeEntriesByRankOrder(prev.map(e =>
            e.encounterType === encounterType && e.position === position
                ? {
                    ...e,
                    [field]: idNumberStr
                        ? licensedPlayersById.get(idNumberStr) || null
                        : null
                }
                : e
        )));
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const sanitizedEntries = sanitizeEntriesByRankOrder(entries);
            setEntries(sanitizedEntries);
            const request = {
                entries: sanitizedEntries.map(e => ({
                    encounterType: e.encounterType,
                    position: e.position,
                    player1IdNumber: e.player1?.idNumber || null,
                    player2IdNumber: e.player2?.idNumber || null,
                })),
            };
            const updated = await api.put(`/league/teams/${teamId}/matches/${matchId}/lineup`, request);
            setEntries(updated?.entries?.map(e => ({...e})) || []);
            setSaved(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p style={{fontSize: 13, color: "#888"}}>Lade Aufstellung...</p>;

    const singles = entries.filter(e => e.encounterType === "EINZEL").sort((a, b) => a.position - b.position);
    const doubles = entries.filter(e => e.encounterType === "DOPPEL").sort((a, b) => a.position - b.position);

    const PlayerSelect = ({encounterType, position, field, value, onChange}) => (
        <select
            value={value || ""}
            onChange={e => onChange(e.target.value || null)}
            style={{
                padding: "4px 8px",
                fontSize: 13,
                border: "1px solid #ddd",
                borderRadius: 4,
                minWidth: 180,
            }}
        >
            <option value="">— nicht besetzt —</option>
            {getSelectablePlayers(encounterType, position, field, value).map(p => (
                <option key={String(p.idNumber)} value={String(p.idNumber)}>
                    {getRankLabel(p.rank)} - {p.name} ({p.idNumber})
                </option>
            ))}
        </select>
    );

    const PlayerDisplay = ({player}) => (
        <span style={{fontSize: 13, color: player ? "#333" : "#aaa"}}>
            {player ? `${player.name} (${player.idNumber})` : "—"}
        </span>
    );

    return (
        <div style={{
            marginTop: 12,
            padding: "12px 14px",
            background: "#f8f9fa",
            borderRadius: 6,
            border: "1px solid #e0e0e0"
        }}>
            <div style={{fontWeight: "bold", fontSize: 13, marginBottom: 10, color: "#333"}}>
                Aufstellung
                {licensedPlayers.length === 0 && (
                    <span style={{fontWeight: "normal", color: "#999", marginLeft: 8, fontSize: 12}}>
                        (keine Lizenzspieler – bitte zuerst synchronisieren)
                    </span>
                )}
            </div>

            {error && <p style={{color: "#c62828", fontSize: 12, marginBottom: 8}}>{error}</p>}

            <div style={{display: "flex", flexDirection: "column", gap: 8}}>
                {singles.map(entry => (
                    <div key={`E${entry.position}`}
                         style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                        <span style={{fontSize: 12, color: "#666", minWidth: 60}}>
                            {ENCOUNTER_LABELS.EINZEL} {entry.position}
                        </span>
                        {isCaptain ? (
                            <PlayerSelect
                                encounterType="EINZEL"
                                position={entry.position}
                                field="player1"
                                value={entry.player1?.idNumber != null ? String(entry.player1.idNumber) : undefined}
                                onChange={val => updateEntry("EINZEL", entry.position, "player1", val)}
                            />
                        ) : (
                            <PlayerDisplay player={entry.player1}/>
                        )}
                    </div>
                ))}

                {doubles.map(entry => (
                    <div key={`D${entry.position}`} style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        flexWrap: "wrap",
                        flexDirection: "column"
                    }}>
                        <span style={{fontSize: 12, color: "#666", minWidth: 60}}>
                            {ENCOUNTER_LABELS.DOPPEL} {entry.position}
                        </span>
                        <div style={{display: "flex", gap: 8, flexWrap: "wrap", paddingLeft: 0}}>
                            {isCaptain ? (
                                <>
                                    <PlayerSelect
                                        encounterType="DOPPEL"
                                        position={entry.position}
                                        field="player1"
                                        value={entry.player1?.idNumber != null ? String(entry.player1.idNumber) : undefined}
                                        onChange={val => updateEntry("DOPPEL", entry.position, "player1", val)}
                                    />
                                    <PlayerSelect
                                        encounterType="DOPPEL"
                                        position={entry.position}
                                        field="player2"
                                        value={entry.player2?.idNumber != null ? String(entry.player2.idNumber) : undefined}
                                        onChange={val => updateEntry("DOPPEL", entry.position, "player2", val)}
                                    />
                                </>
                            ) : (
                                <>
                                    <PlayerDisplay player={entry.player1}/>
                                    {entry.player2 && <span style={{fontSize: 13, color: "#666"}}>/</span>}
                                    <PlayerDisplay player={entry.player2}/>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {isCaptain && (
                <div style={{marginTop: 12, display: "flex", alignItems: "center", gap: 10}}>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                            padding: "6px 16px",
                            fontSize: 13,
                            background: "#4CAF50",
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                        }}
                    >
                        {saving ? "Speichern..." : "Aufstellung speichern"}
                    </button>
                    {saved && <span style={{color: "#4CAF50", fontSize: 12}}>✓ Gespeichert</span>}
                </div>
            )}
        </div>
    );
};

export default MatchLineupEditor;
